import { useDisclosureStore } from '@ariakit/react';
import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo } from 'react';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { ConfigOverrideValue } from '#components/patterns/config-overrides/ConfigOverrideValue.tsx';
import { CONFIG_OVERRIDE_LABELS } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { UnknownConfigOverridesList } from '#components/patterns/config-overrides/unknown/UnknownConfigOverridesList.tsx';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import { Table } from '#components/patterns/table/Table.tsx';
import type { ConfigOverride } from '#dn-types/host.ts';
import { useSortedConfigOverrides } from '#hooks/useSortedConfigOverrides.ts';
import type { UnknownConfigOverride } from '#util/unknownConfigOverrides.ts';
import { StatsDisclosureContent } from './StatsDisclosureContent.tsx';
import { StatsDisclosureTop } from './StatsDisclosureTop.tsx';
import styles from './ConfigOverridesList.module.css';

type Item = ConfigOverride;

type Props = {
  items: Item[];
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  /**
   * Overrides with no editor here, including ones pending delete; rendered read-only and
   * delete-only below the editable rows.
   */
  unknownItems?: UnknownConfigOverride[];
  /** Indices into `unknownItems` that are pending delete */
  removedUnknownIndices?: ReadonlySet<number>;
  onRemoveUnknown?: (index: number) => void;
  onRestoreUnknown?: (index: number) => void;
  children?: React.ReactNode;
};

const columnHelper = createColumnHelper<Item>();

export function ConfigOverridesList({
  onRemove,
  onEdit,
  items,
  unknownItems,
  removedUnknownIndices,
  onRemoveUnknown,
  onRestoreUnknown,
  children,
}: Props) {
  const statsDisclosure = useDisclosureStore();

  const sortedConfigOverrides = useSortedConfigOverrides(items);
  const statsType = items.find(({ key }) => key === 'stats.type');

  const columns = useMemo(
    () => [
      columnHelper.accessor('key', {
        header: 'Option',
        cell(info) {
          const key = info.getValue();
          if (key === 'stats.type') {
            return <StatsDisclosureTop disclosure={statsDisclosure} />;
          }
          if (key === 'lighthouse.local_allow_list') {
            return 'Local allow rule';
          }
          if (key === 'lighthouse.remote_allow_list') {
            return 'Remote allow rule';
          }

          return <>{CONFIG_OVERRIDE_LABELS[key]}</>;
        },
      }),
      columnHelper.accessor('value', {
        header: 'Value',
        size: 200,
        cell(info) {
          const configOverride = info.row.original;
          return <ConfigOverrideValue configOverride={configOverride} usePills={true} />;
        },
      }),
      columnHelper.display({
        id: 'config-override-action-menu',
        header: '',
        size: 34,
        cell(info) {
          return (
            <div className="flex justify-end">
              <MeatballMenu>
                <MenuItem
                  onClick={() => {
                    const index = items.findIndex(
                      (i) => i.key === info.row.original.key && i.value === info.row.original.value
                    );
                    onEdit(index);
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const index = items.findIndex(
                      (i) => i.key === info.row.original.key && i.value === info.row.original.value
                    );
                    onRemove(index);
                  }}
                >
                  Delete
                </MenuItem>
              </MeatballMenu>
            </div>
          );
        },
      }),
    ],
    [onEdit, onRemove, statsDisclosure, items]
  );

  const hasUnknownItems = Boolean(unknownItems?.length);

  return (
    <div>
      {items.length ?
        <div className={cn('mb-16', styles.ConfigOverridesList_wrapper)}>
          <Table
            className={styles.ConfigOverridesList}
            data={statsType ? [...sortedConfigOverrides, statsType] : sortedConfigOverrides}
            columns={columns}
          />
          <StatsDisclosureContent configOverrides={items} disclosure={statsDisclosure} />
        </div>
      : hasUnknownItems ?
        null
      : <Banner className="mb-16">No config defined</Banner>}
      {children}
      {unknownItems?.length ?
        <UnknownConfigOverridesList
          className="mt-24"
          heading="Unsupported config"
          items={unknownItems}
          removedIndices={removedUnknownIndices}
          onRemove={onRemoveUnknown}
          onRestore={onRestoreUnknown}
        />
      : null}
    </div>
  );
}
